/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Toolbar,
  IconButton,
  AppBar,
  Avatar,
  ButtonBase,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PolicyIcon from "@mui/icons-material/Policy";
import AddBoxIcon from "@mui/icons-material/AddBox";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CircularProgress from "@mui/material/CircularProgress";
import PaymentsIcon from "@mui/icons-material/Payments";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Atras from "./atras";
import { auth, uploadImage, updateUserProfile } from "../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { endSession, isLoggedIn } from "../session";
import { Link, useNavigate } from "react-router-dom";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import logo from "../imgs/wandr-mark.svg";
import BottomNav from "./BottomNav";

const Perfil = () => {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [uid, setUid] = useState("");
  const [loading, setLoading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null); // Estado para la URL de la imagen de perfil
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const { uid } = user;
          const db = getFirestore();
          const userDocRef = doc(db, "users", uid);

          const userDocSnapshot = await getDoc(userDocRef);

          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            const userName = userData.displayName || "Usuario";
            const userEmail = userData.email || "";
            const uid = userData.uid || "";
            setEmail(userEmail);
            setDisplayName(userName, userEmail);
            setUid(uid);
            if (userData.profileImageUrl) {
              setProfileImageUrl(userData.profileImageUrl);
            }
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        // Si el usuario cierra sesión, podemos limpiar el estado del displayName y de la imagen de perfil
        setDisplayName("");
        setEmail("");
        setProfileImageUrl(null);
      }
    });

    return unsubscribe;
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    setImageError("");
    if (!file) return;
    // Validar tipo de archivo
    if (!file.type.startsWith("image/")) {
      setImageError("Solo se permiten imágenes.");
      return;
    }
    // Validar tamaño (máx. 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setImageError("La imagen supera el tamaño máximo de 10MB.");
      return;
    }
    try {
      setLoading(true);
      const imageUrl = await uploadImage(file);
      const user = auth.currentUser;
      if (user) {
        const uid = user.uid;
        await updateUserProfile(uid, { profileImageUrl: imageUrl });
        setProfileImageUrl(imageUrl);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      setImageError("No se pudo subir la foto de perfil. Verifica tu conexión o permisos de Firebase Storage.");
      setLoading(false);
    }
  };

  let navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, [navigate]);

  const onLogout = async () => {
    try {
      await endSession();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  const styles = {
    cont: {
      position: "absolute",
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      margin: 0,
      padding: 0,
      boxSizing: "border-box",
      backgroundColor: "#06110d",
      height: "100vh",
    },
    user: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      width: "100%",
      height: "90px",
      marginTop: "10px",
      color: "black",
    },
    progress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: "-20px",
      marginLeft: "-20px",
      color: "white",
    },
    uploadButton: {
      marginTop: "20px",
      color: "white",
    },
  };

  const menuItems = [
    { label: "Editar Perfil", icon: <SettingsIcon />, to: "/edit" },
    { label: "Mis propiedades", icon: <AddBoxIcon />, to: "/myProperties" },
    { label: "Soporte", icon: <SupportAgentIcon />, to: "/support" },
    { label: "Política y Privacidad", icon: <PolicyIcon />, to: "/policy" },
    { label: "Términos de Servicio", icon: <AutoStoriesIcon />, to: "/terms" },
    { label: "Cerrar sesión", icon: <ExitToAppIcon />, onClick: onLogout, danger: true },
  ];

  return (
    <Container>
      <AppBar position="fixed" sx={{ bgcolor: "#0d2219" }}>
        <Toolbar
          sx={{
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <Atras color="inherit" />
          {profileImageUrl && (
            <Avatar
              sx={{
                marginLeft: 2,
                padding: "0px",
                border: "2.5px solid white",
              }}
              alt="Perfil"
              src={profileImageUrl}
              style={styles.avatar}
            />
          )}
          <div style={{ position: "relative" }}>
            <input
              type="file"
              accept="image/*"
              onClick={(event) => (event.target.value = null)}
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="upload-button"
            />
            <IconButton
              variant="contained"
              component="span"
              sx={styles.uploadButton}
              onClick={() => document.getElementById("upload-button").click()}
              color="black"
            >
              <AddPhotoAlternateIcon />
              {loading && (
                <CircularProgress
                  size={40}
                  thickness={4}
                  style={styles.progress}
                />
              )}
            </IconButton>
          </div>
          <Container style={styles.user}>
            <Typography variant="h5" color="white">
              {displayName}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: "12px", color: "white" }}
              gutterBottom
            >
              {email}
            </Typography>
          </Container>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 17, mb: 14 }}>
        {imageError && (
          <Typography color="error" sx={{ mb: 2, fontWeight: 600 }}>
            {imageError}
          </Typography>
        )}
        {menuItems.map(({ label, icon, to, onClick, danger }) => (
          <Paper key={label} sx={{ mt: 2.5, borderRadius: 3, overflow: "hidden" }}>
            <ButtonBase
              component={to ? Link : "button"}
              to={to}
              onClick={onClick}
              sx={{
                width: "100%",
                height: 64,
                px: 2.5,
                gap: 2,
                justifyContent: "flex-start",
                textAlign: "left",
                color: danger ? "error.main" : "text.primary",
                transition: "background-color 0.2s ease",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {icon}
              <Typography variant="h6" sx={{ flexGrow: 1, fontSize: "1.1rem" }}>
                {label}
              </Typography>
              <ChevronRightIcon sx={{ opacity: 0.5 }} />
            </ButtonBase>
          </Paper>
        ))}
      </Container>
      <FloatingWhatsApp
        phoneNumber="+542323680787"
        placeholder="Escribe un mensaje..."
        statusMessage="Responde en menos de 10 minutos"
        accountName="Wandr"
        avatar={logo}
        chatMessage="Hola!¿Cómo estás?¿En que puedo ayudarte?😃"
      />
      <BottomNav />
    </Container>
  );
};

export default Perfil;
